import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent double execution in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      console.log('[AuthCallback] Starting auth processing...');
      console.log('[AuthCallback] Full URL:', window.location.href);
      
      // Try multiple methods to extract session_id (mobile browsers handle URLs differently)
      let sessionId = null;
      
      // Method 1: Check window.location.hash
      const hash = window.location.hash;
      console.log('[AuthCallback] Hash:', hash);
      if (hash) {
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        if (sessionIdMatch) {
          sessionId = sessionIdMatch[1];
          console.log('[AuthCallback] Found session_id in hash');
        }
      }
      
      // Method 2: Check URL search params
      if (!sessionId) {
        const urlParams = new URLSearchParams(window.location.search);
        sessionId = urlParams.get('session_id');
        if (sessionId) {
          console.log('[AuthCallback] Found session_id in search params');
        }
      }
      
      // Method 3: Check location.hash from react-router
      if (!sessionId && location.hash) {
        const hashMatch = location.hash.match(/session_id=([^&]+)/);
        if (hashMatch) {
          sessionId = hashMatch[1];
          console.log('[AuthCallback] Found session_id in location.hash');
        }
      }
      
      // Method 4: Parse the entire URL manually (for edge cases on mobile)
      if (!sessionId) {
        const fullUrl = window.location.href;
        const sessionMatch = fullUrl.match(/session_id=([^&\s#]+)/);
        if (sessionMatch) {
          sessionId = sessionMatch[1];
          console.log('[AuthCallback] Found session_id via full URL parse');
        }
      }
      
      if (!sessionId) {
        console.log('[AuthCallback] No session_id found, redirecting to /');
        navigate('/');
        return;
      }

      console.log('[AuthCallback] Using session_id:', sessionId.substring(0, 20) + '...');

      try {
        console.log('[AuthCallback] Calling /api/auth/session...');
        const response = await axios.post(
          `${API_URL}/api/auth/session`,
          { session_id: sessionId },
          { 
            withCredentials: true,
            timeout: 15000,
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        console.log('[AuthCallback] Session response status:', response.status);
        console.log('[AuthCallback] Session created successfully:', response.data.email);
        console.log('[AuthCallback] User role:', response.data.role);
        console.log('[AuthCallback] User status:', response.data.status);
        
        // Check if user is pending
        if (response.data.status === 'pending') {
          console.log('[AuthCallback] User account is pending approval');
          alert('Your account is pending approval. Please contact an administrator.');
          navigate('/');
          return;
        }
        
        // Store session token for mobile browsers that block cookies
        if (response.data.session_token) {
          try {
            localStorage.setItem('session_token', response.data.session_token);
            console.log('[AuthCallback] Session token stored in localStorage');
          } catch (e) {
            console.log('[AuthCallback] Could not store token in localStorage:', e);
          }
        } else {
          console.log('[AuthCallback] WARNING: No session_token in response');
        }
        
        // Remove session_token from user data before setting
        const { session_token, ...userData } = response.data;
        setUser(userData);
        
        // Store user data in sessionStorage as backup
        try {
          sessionStorage.setItem('user_data', JSON.stringify(userData));
          console.log('[AuthCallback] User data stored in sessionStorage');
        } catch (e) {
          console.log('[AuthCallback] Could not store in sessionStorage:', e);
        }
        
        // Clear the hash and navigate to dashboard
        window.history.replaceState(null, '', '/dashboard');
        
        // Use setTimeout to ensure state updates complete before navigation
        console.log('[AuthCallback] Navigating to dashboard...');
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 100);
      } catch (error) {
        console.error('[AuthCallback] Auth error status:', error.response?.status);
        console.error('[AuthCallback] Auth error detail:', error.response?.data?.detail || error.message);
        console.error('[AuthCallback] Full error:', error);
        
        // Show user-friendly error
        const errorMsg = error.response?.data?.detail || 'Authentication failed. Please try again.';
        alert(errorMsg);
        navigate('/');
      }
    };

    // Longer delay for mobile to ensure page is fully loaded
    setTimeout(processAuth, 200);
  }, [navigate, setUser, location.hash]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
